import { useState, useMemo } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import {
  DeleteForever as TrashIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { GlobalButton, AutocompleteInput } from '@/components';
import { GlobalButtonVariant } from '@/components/buttons/GlobalButton';
import { Table } from '@/components';
import { assetSelectorColumns } from '../config/AssetsColumns';
import { useAssetsOptions } from '../pages/Assignments/hooks';
import type { AssetApi } from '../pages/Assets/models';
import { TypeAutocompleteInput } from '../pages/Assets/Types/components';
import { BrandAutocompleteInput } from '../pages/Assets/Brands/components';
import { AssetModelAutocompleteInput } from '../pages/Assets/AssetModels/components';
import { Option } from '@/components/inputs/AutocompleteInput';
import { useAutocomplete } from '@/hooks';

interface AssetSelectorProps {
  open: boolean;
  onClose: () => void;
  onSelect: (asset: AssetApi) => void;
}

const AssetSelector = ({ open, onClose, onSelect }: AssetSelectorProps) => {
  const [typeId, setTypeId] = useState('');
  const [brandId, setBrandId] = useState('');
  const [modelId, setModelId] = useState('');
  const [barcodeSearch, setBarcodeSearch] = useState('');
  const [selectedBarcodeAsset, setSelectedBarcodeAsset] = useState<Option | null>(null);

  const resetFilter = () => {
    setTypeId('');
    setBrandId('');
    setModelId('');
    setBarcodeSearch('');
    setSelectedBarcodeAsset(null);
  };

  const { assetsOptions, rawAssets } = useAssetsOptions({
    type: typeId,
    brand: brandId,
    model: modelId,
    returnRaw: true,
  });

  const handleBarcodeSelect = (option: Option | null) => {
    setSelectedBarcodeAsset(option);
    if (option?.data) {
      setBarcodeSearch(option.data.barcode || option.data.serialNumber || '');
    }
  };

  const barcodeFilteredAssets = rawAssets?.filter(asset => {
    if (!barcodeSearch.trim()) return false;
    
    const searchTerm = barcodeSearch.trim().toLowerCase();
    const barcode = asset.barcode?.toLowerCase() || '';
    return barcode.includes(searchTerm);
  }) || [];

  const { options: barcodeOptions } = useAutocomplete({
    data: barcodeFilteredAssets,
    getLabel: (asset) => `${asset.barcode} - ${asset.assetType?.name || ''} ${asset.brand?.name || ''} ${asset.model?.name || ''}`,
  });

  const typeIdToName = useMemo(() => {
    const mapping: Record<string, string> = {};
    rawAssets?.forEach(asset => {
      if (asset.assetType) {
        mapping[asset.assetType.id] = asset.assetType.name;
      }
    });
    return mapping;
  }, [rawAssets]);

  const brandIdToName = useMemo(() => {
    const mapping: Record<string, string> = {};
    rawAssets?.forEach(asset => {
      if (asset.brand) {
        mapping[asset.brand.id] = asset.brand.name;
      }
    });
    return mapping;
  }, [rawAssets]);

  const modelIdToName = useMemo(() => {
    const mapping: Record<string, string> = {};
    rawAssets?.forEach(asset => {
      if (asset.model) {
        mapping[asset.model.id] = asset.model.name;
      }
    });
    return mapping;
  }, [rawAssets]);

  const filteredAssetsOptions = assetsOptions.filter(opt => opt.data.status !== 'in_service');

  const barcodeFilteredTableOptions = useMemo(() => {
    if (!barcodeSearch.trim()) return filteredAssetsOptions;
    
    return filteredAssetsOptions.filter(option => 
      option.data.barcode?.toLowerCase().includes(barcodeSearch.toLowerCase())
    );
  }, [filteredAssetsOptions, barcodeSearch]);

  const tableData = barcodeFilteredTableOptions.map(({ data }) => {
    return {
      ...data,
      type: data.typeDetails?.name || data.assetType?.name || data.type || '',
      userDisplay: data.user?.fullName || 'No asignado',
    };
  });
  const columns = assetSelectorColumns;

  return (
    <Dialog open={open} onClose={onClose} maxWidth='lg' fullWidth>
      <DialogTitle className='bg-[#FF7500] text-white relative'>
        <div className='flex justify-between items-center'>
          <span>Buscar y seleccionar equipo</span>
          <GlobalButton 
            variant={GlobalButtonVariant.secondaryButton}
            action={onClose}
          >
            <CloseIcon className='text-white'/>
          </GlobalButton>
        </div>
      </DialogTitle>
      <DialogContent className='bg-white'>
        <div className='w-full mt-4 p-5 flex flex-col gap-4 bg-white rounded shadow-md'>
          <div className='flex gap-3'>
            <AutocompleteInput
              name='barcode-search'
              placeholder='Buscar por código de barras'
              value={selectedBarcodeAsset}
              onChange={handleBarcodeSelect}
              onInputChange={setBarcodeSearch}
              options={barcodeOptions}
            />
          </div>
          
          <div className='flex gap-3'>
            <TypeAutocompleteInput
              name='type-autocomplete'
              placeholder='Tipo'
              value={typeId ? { label: typeIdToName[typeId] || typeId, data: typeId } : null}
              onChange={(option) => setTypeId(option ? option.data : '')}
              onInputChange={() => {}}
            />
            <BrandAutocompleteInput
              name='brand-autocomplete'
              placeholder='Marca'
              value={brandId ? { label: brandIdToName[brandId] || brandId, data: brandId } : null}
              onChange={(option) => setBrandId(option ? option.data : '')}
              onInputChange={() => {}}
            />
            <AssetModelAutocompleteInput
              name='model-autocomplete'
              placeholder='Modelo'
              value={modelId ? { label: modelIdToName[modelId] || modelId, data: modelId } : null}
              onChange={(option) => setModelId(option ? option.data : '')}
              onInputChange={() => {}}
              brandId={brandId}
            />
          </div>
        </div>
        <div className='w-full p-5 flex flex-row gap-4 bg-white justify-end rounded shadow-md mb-4'>
          <GlobalButton
            variant={GlobalButtonVariant.secondaryButton}
            action={resetFilter}
          >
            <TrashIcon color='primary' />
            Limpiar
          </GlobalButton>
        </div>
        {!typeId && !brandId && !modelId && !barcodeSearch ? (
          <div className='w-full text-center text-gray-500 py-8'>
            Seleccione al menos un filtro o ingrese un código de barras para buscar un equipo
          </div>
        ) : tableData.length === 0 ? (
          <div className='w-full text-center text-gray-500 py-8'>
            <div className='mb-2'>
              <span className='text-lg'>🔍</span>
            </div>
            <div className='text-lg font-medium mb-2'>
              No se encontraron equipos
            </div>
            <div className='text-sm text-gray-400'>
              No hay equipos disponibles con los filtros seleccionados
            </div>
          </div>
        ) : (
          <div style={{ marginTop: 16, maxHeight: 300, overflowY: 'auto' }}>
            <Table
              columns={columns}
              data={tableData}
              renderActions={(rowData) => (
                <GlobalButton
                  variant={GlobalButtonVariant.primaryButton}
                  action={() => {
                    onSelect(rowData);
                    onClose();
                  }}
                >
                  Seleccionar
                </GlobalButton>
              )}
              isLoading={false}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AssetSelector;
